export const ROLES = [
  "admin",
  "site_admin",
  "moderator",
  "event_manager",
  "contributor",
  "viewer",
] as const;
export type Role = (typeof ROLES)[number];

type Action =
  | "post:read"
  | "post:create"
  | "post:edit:own"
  | "post:edit:any"
  | "post:delete:own"
  | "post:delete:any"
  | "media:view"
  | "media:upload"
  | "media:tag"
  | "tree:view"
  | "tree:edit"
  | "members:invite"
  | "members:view"
  | "members:approve"
  /** Link/relink a member to their person in the family tree. Split out of
   *  members:manage so moderators can fix identity links without getting
   *  role changes or member removal. */
  | "members:link-tree"
  | "members:manage"
  | "settings:edit"
  | "sessions:view"
  | "stats:view"
  | "events:view"
  | "events:rsvp"
  | "events:manage"
  | "events:delete:own"
  | "events:delete:any"
  /** Run a live reunion: start and end the 1:1 rounds. */
  | "live:host"
  | "trash:manage";

const GRANTS: Record<Role, readonly Action[]> = {
  viewer: [
    "post:read",
    "media:view",
    "tree:view",
    "events:view",
    "events:rsvp",
  ],
  event_manager: [
    "post:read",
    "media:view",
    "tree:view",
    "events:view",
    "events:rsvp",
    "events:manage",
    "events:delete:own",
    "events:delete:any",
    "live:host",
  ],
  contributor: [
    "post:read",
    "post:create",
    "post:edit:own",
    "post:delete:own",
    "media:view",
    "media:upload",
    "media:tag",
    "tree:view",
    "events:view",
    "events:rsvp",
    "events:delete:own",
  ],
  // Content moderator: full moderation over posts, media, tree, and events,
  // plus inviting members, approving pending users (members:view +
  // members:approve; the API caps the roles they may grant to
  // contributor/viewer) and linking members to their tree person — but none
  // of the administrative powers (members:manage, settings:edit,
  // sessions:view, stats:view, trash:manage).
  moderator: [
    "post:read",
    "post:create",
    "post:edit:own",
    "post:edit:any",
    "post:delete:own",
    "post:delete:any",
    "media:view",
    "media:upload",
    "media:tag",
    "tree:view",
    "tree:edit",
    "members:invite",
    "members:view",
    "members:approve",
    "members:link-tree",
    "events:view",
    "events:rsvp",
    "events:manage",
    "events:delete:own",
    "events:delete:any",
    "live:host",
  ],
  site_admin: [
    "post:read",
    "post:create",
    "post:edit:own",
    "post:edit:any",
    "post:delete:own",
    "post:delete:any",
    "media:view",
    "media:upload",
    "media:tag",
    "tree:view",
    "tree:edit",
    "members:invite",
    "events:view",
    "events:rsvp",
    "events:manage",
    "events:delete:own",
    "events:delete:any",
  ],
  admin: [
    "post:read",
    "post:create",
    "post:edit:own",
    "post:edit:any",
    "post:delete:own",
    "post:delete:any",
    "media:view",
    "media:upload",
    "media:tag",
    "tree:view",
    "tree:edit",
    "members:invite",
    "members:view",
    "members:approve",
    "members:link-tree",
    "members:manage",
    "settings:edit",
    "sessions:view",
    "stats:view",
    "events:view",
    "events:rsvp",
    "events:manage",
    "events:delete:own",
    "events:delete:any",
    "live:host",
    "trash:manage",
  ],
};

export function can(role: Role, action: Action): boolean {
  return GRANTS[role].includes(action);
}
